//------------------------------------------------------------------------
// Stacked Horizontal Bar Chart
//------------------------------------------------------------------------
// NOTE: Only import the necessary modules
// https://www.chartjs.org/docs/latest/getting-started/integration.html
// NOTE: Treeshaking doesn’t seem to be working when importing everything
// https://github.com/chartjs/Chart.js/issues/10163
// e.g. import Chart from 'chart.js/auto';
/* eslint no-unused-vars: "off" */
import {Chart, BarElement, BarController, Tooltip, LinearScale, CategoryScale} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { cursorPositioner, axis_font_opts } from '../helpers/chart-js-helpers';
import debounce from "lodash/debounce";

Chart.register(BarElement, BarController, Tooltip, LinearScale, CategoryScale);

/**
 * Custom positioner - Allows for the tooltip to follow the cursor's pointer
 *
 * Notes:
 * - https://www.chartjs.org/docs/latest/configuration/tooltip.html#custom-position-modes
 * - https://codepen.io/AZIIZoom/pen/eYzJNZW?editors=1010
 * @function Tooltip.positioners.myCustomPositioner
 * @param elements {Chart.Element[]} the tooltip elements
 * @param eventPosition {Point} the position of the event in canvas coordinates
 * @returns {TooltipPosition} the tooltip position
 */
Tooltip.positioners.cursor = cursorPositioner;

class StackedHorizontalBarChart {
  constructor(config) {
    this.el = config.el;
    this.canvas = this.el.querySelector('canvas');
    this.legendItems = this.el.querySelectorAll('[data-legend-item]');

    if (!this.canvas) {
      console.error(`Unable to initialize vertical bar chart, no matching element for ${this.el}.`);
      return false;
    }

    this.setData();
    this.setChartConfig();
    this.init();
  }

  init() {
    this.chart = new Chart(this.canvas, {
      type: 'bar',
      data: this.data,
      plugins: this.plugins,
      options: this.chartOpts
    });

    this.el.addEventListener('mouseout-chart', debounce(this.mouseoutChartHandler.bind(this), 100));

    this.el.addEventListener('mouseover-chart', debounce(this.mouseoverChartHandler.bind(this), 100));
  }

  /**
   * Retrieves data-* attributes from the Chart's container and create instance variables
   * of each in the correct format for Chart.js
   *
   * These are the data-* attributes that are being used:
   * - data-segments = the segments from the block's segments repeater field
   * - data-title = the chart's title
   * - data-type = the type of circle chart that is being used
   *
   * isntance variables that are being created:
   * - this.dataValues
   * - this.colors
   * - this.title
   * - this.type
   * @return null
   */
  setData() {
    let data = JSON.parse(this.el.getAttribute('data-segments'));

    this.dataLabels = JSON.parse(this.el.getAttribute('data-labels'));
    this.dataColors = data.map(item => item.color + 'e0');

    this.datasets = data.map((item) => {
      let color = item.color + 'e0';

      return {
        label: item.name,
        backgroundColor: color,
        borderColor: '#fff',
        borderWidth: 2,
        barPercentage: .5,
        maxBarThickness: 42,
        categoryPercentage: 0.9,
        data: item.values.map(item => parseInt(item.value, 10))
      };
    });

    this.data = {
      labels: this.dataLabels,
      datasets: this.datasets
    };
  }

  setChartConfig() {
    const userDefinedOpts = JSON.parse(this.el.getAttribute('data-chart-opts'));

    // Move horizontal bar position to accommodate for Label
    // source: https://www.youtube.com/watch?v=rW_-WXuhxp8&t=623s
    const barPosition = {
      id: 'barPosition',
      beforeDatasetsDraw: (chart, args, pluginOptions) => {
        const {data, chartArea } = chart;
        const { top, height } = chartArea;
        const barsCount = chart.data.datasets.length;
        const barHeight = height / barsCount;

        for (let i = 0; i < barsCount; i++) {
          chart.getDatasetMeta(i).data.forEach((barElement, index) => {
            const magicNumber = 0.7;
            barElement.y = top + (barHeight * (index + magicNumber));
            // console.log(barElement);
          });
        }
      }
    };

    let mql = window.matchMedia("(min-width: 700px)");

    mql.addEventListener('change', () => {
      // Returning the same values that onResize would for chart.js
      let width  = window.innerWidth;
      let tooltip = (width > 700) ? this.externalTooltipHandler.bind(this) : false;

      this.chart.options.plugins.tooltip.external = tooltip;
      this.chart.update();
    });

    let datalabelsFontSize = (window.innerWidth > 700) ? 22 : 18;
    let tickMarkFontSize = (window.innerWidth > 700) ? 16 : 14;
    let chartFontColor = "#404040"; // $c-default / fs-color("text", "gray-dark");

    // Set opts for chart.js
    this.chartOpts = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: userDefinedOpts.y_axis_title ? true : false, // displays y axis title via user specified value
            text: userDefinedOpts.y_axis_title, // displays y axis title via user specified value
            font: axis_font_opts(),
            color: chartFontColor
          },
          ticks: {
            display: true, // displays y axis ticks, e.g. the step counts
            font: {
              size: tickMarkFontSize,
              family: "'Luma Sans', 'Helvetica', 'Arial', sans-serif",
              weight: 'normal'
            },
            color: chartFontColor,
            position: 'top',
            mirror: true,
            labelOffset: -15
          },
          grid: {
            display: false, // shows or hides the horizontal gridlines on the chart
          },
          stacked: true
        },
        x: {
          position: 'top',
          border: {
            displaay: false
          },
          title: {
            display: userDefinedOpts.x_axis_title ? true : false,
            text: userDefinedOpts.x_axis_title,
            font: axis_font_opts(),
            color: chartFontColor
          },
          ticks: {
            display: userDefinedOpts.show_x_axis, // setting false hides the values on the x axis
            font: {
              size: tickMarkFontSize,
              family: "'Luma Sans', 'Helvetica', 'Arial', sans-serif",
              weight: 'normal'
            },
            color: chartFontColor
          },
          grid: {
            display: userDefinedOpts.show_gridlines, // shows or hides the horizontal gridlines on the chart
            drawOnChartArea: userDefinedOpts.show_gridlines,
            drawTicks: false // remove the ticks that would point to the number on the x axis
          },
          stacked: true,
          grace: '2%'
        },
        // Displays a line at the bottom of the chart to mimic the design
        x1: {
          position: 'bottom',
          grid: {
            display: true, // shows or hides the horizontal gridlines on the chart
            drawOnChartArea: false,
            drawTicks: false // remove the ticks that would point to the number on the x axis
          },
          ticks: {
            display: false
          }
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: false,
          position: 'cursor',
          // We are binding the method to have access to this.toolTipEl
          external: mql.matches ? this.externalTooltipHandler.bind(this) : false,
        }
      },
    };

    // Add Chart.js plugins here
    this.plugins = [barPosition];

    // Add data labels if user wants it to appear by checking the show_data_series_count parameter
    if (userDefinedOpts.show_data_series_counts) {
      this.plugins.push(ChartDataLabels);
      this.chartOpts.plugins.datalabels = {
        font: {
          family: "'Luma Sans', 'Helvetica', 'Arial', sans-serif",
          size: datalabelsFontSize,
          lineHeight: 1.3,
          weight: 'bold',
        },
        color: '#480751', // fs-color("text", "purple")
        anchor: 'end',
        clamp: true, // only put the data label inside of the chart
        align: 'start',
        // Ensures that only one the total sum of the bar is displayed at the top of the bar
        // https://www.youtube.com/watch?v=mlOlkuuh_E8
        formatter: (value, context) => {
          const { chart } = context;
          const data = []; // all of the data for the entire bar

          chart.data.datasets.forEach((dataset) => {
            let index = context.dataIndex; // the index of the segment that is being displayed
            if (dataset.data[index] == undefined) {
              return;
            }
            data.push(dataset.data[index]);
          });

          let totalSum = data.reduce((previousValue, currentValue) => previousValue + currentValue, 0);

          // The datasetIndex is the bar's segment index. We only want the last segment to display
          // the total value instead of each. So we get the number of segments a bar should have by getting length
          // and compare it to the current segment that is being rendered. If it is the last, we display it.
          if (context.datasetIndex === chart.data.datasets.length - 1) {
            return totalSum;
          } else {
            return '';
          }
        },
        offset: this.el.hasAttribute('data-label-offset') ? parseFloat(this.el.getAttribute('data-label-offset'), 10) : -60
      }
    }
  }

  mouseoutChartHandler() {
    this.legendItems.forEach(item => item.classList.remove('is-inactive'));
  }

  mouseoverChartHandler(evt) {
    const { tooltip } = evt.detail;
    const selectedIndex = tooltip.dataPoints[0].datasetIndex;

    this.legendItems.forEach(item => {
      let legendIndex = item.getAttribute('data-index');

      if (parseInt(legendIndex, 10) === parseInt(selectedIndex, 10)) {
        item.classList.remove('is-inactive');
      } else {
        item.classList.add('is-inactive');
      }
    });
  }

  externalTooltipHandler(context) {
    const { chart, tooltip } = context;
    const tooltipEl = chart.canvas.nextElementSibling;

    // Custom event detail that is being passed through
    let detail = {
      detail: {
        el: this.el,
        tooltip,
        chart
      }
    };

    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = 0;
      const mouseoutChart = new CustomEvent("mouseout-chart", detail);

      this.el.dispatchEvent(mouseoutChart);
      return;
    }

    // Set Text
    if (tooltip.dataPoints) {
      const { dataIndex } = tooltip;
      const tooltipFragment = document.createDocumentFragment();
      let dataPoints = tooltip.dataPoints || [];

      dataPoints.forEach(item => {
        // Add all of the segments of a bar into a single array
        let segmentNums = [];
        chart.data.datasets.forEach((dataset) => {
          let index = item.dataIndex; // the index of the segment that is being displayed
          if (dataset.data[index] == undefined) {
            return;
          }
          segmentNums.push(dataset.data[index]);
        });

        // get the total sum of the bar
        let totalSum = segmentNums.reduce((previousValue, currentValue) => previousValue + currentValue, 0);

        // The total sum elements and text node
        let totalValueEl = document.createElement('span');
        let totalValueText = document.createTextNode(totalSum);

        // The bar labels
        let titleEl = document.createElement('span');
        let titleText = document.createTextNode(item.label);

        // The segment label
        let segmentLabelEl = document.createElement('span');
        let segmentLabelText = document.createTextNode(item.dataset.label);

        // The segment value
        let segmentValueEl = document.createElement('span');
        let segmentValueText = document.createTextNode(item.formattedValue);

        // The segment "out of" value, e.g. 8 / 22 - the " / 22" is the value
        let segmentBaseValueEl = document.createElement('span');
        let segmentBaseValueText = document.createTextNode('/' + totalSum);

        titleEl.classList.add('ChartTooltip-title');
        totalValueEl.classList.add('ChartTooltip-value');
        segmentLabelEl.classList.add('ChartTooltip-title');
        segmentValueEl.classList.add('ChartTooltip-value');
        segmentBaseValueEl.classList.add('ChartTooltip-base');

        titleEl.appendChild(titleText);
        totalValueEl.appendChild(totalValueText);
        segmentLabelEl.appendChild(segmentLabelText);
        segmentValueEl.appendChild(segmentValueText);
        segmentBaseValueEl.appendChild(segmentBaseValueText);

        segmentValueEl.appendChild(segmentBaseValueEl);
        tooltipFragment.appendChild(titleEl);
        tooltipFragment.appendChild(totalValueEl);
        tooltipFragment.appendChild(segmentLabelEl);
        tooltipFragment.appendChild(segmentValueEl);
      });

      while(tooltipEl.firstChild) {
        tooltipEl.firstChild.remove();
      }

      tooltipEl.appendChild(tooltipFragment);
    }

    const { offsetLeft, offsetTop } = chart.canvas;
    tooltipEl.style.opacity = 1;
    tooltipEl.style.left = offsetLeft + (tooltip.caretX + 10) + 'px';
    tooltipEl.style.top = offsetTop + (tooltip.caretY + 10) + 'px';

    // Emit event when we are hovering over the segment
    const mouseoverChart = new CustomEvent("mouseover-chart", detail);
    this.el.dispatchEvent(mouseoverChart);
  }
}

const init = () => {
  const stackedCharts = document.querySelectorAll('[data-stacked-horizontal-bar-chart]');

  if (stackedCharts) {
    stackedCharts.forEach(chart => {
      new StackedHorizontalBarChart({el: chart});
    })
  }
}

// Init on initial page load
init();
